<template>
  <div>
    <CardBase :headerBorder="false">
      <template #header>{{ $route.meta.title }}</template>
      <template #toolbar>
        <router-link
          v-if="userCan('create.helpdesk')"
          :to="{ name: 'SupportTicketCreate' }"
          class="button button--primary poppins"
        >
          <font-awesome-icon :icon="['fas', 'plus-circle']" class="mr-1" />New
          Ticket
        </router-link>
      </template>
      <template #content>
        <form
          class="form form-search"
          method="get"
          @submit.prevent="reloadTable"
        >
          <div class="form-search__row">
            <div class="form-search__double">
              <label class="form__label">Search</label>
              <input
                type="text"
                class="form__input"
                placeholder="Support Ticket Number / Username"
                v-model="formData.search"
              />
            </div>
            <div class="form-search__double">
              <label class="form__label">Date Order</label>
              <select class="form__input" v-model="tableData.sort_dir">
                <option value="asc">Oldest First</option>
                <option value="desc">Newest First</option>
              </select>
            </div>
          </div>

          <div class="form-search__row">
            <div class="form-search__double">
              <label class="form__label">Start Date</label>
              <date-picker
                v-model="formData.start_date"
                type="date"
                :editable="false"
                :clearable="false"
                input-class="form__input"
                value-type="YYYY-MM-DD"
                format="DD MMMM YYYY"
                placeholder="Start Date"
                @close="checkEndDate"
                :disabled-date="notAfterToday"
              ></date-picker>
            </div>
            <div class="form-search__double">
              <label class="form__label">End Date</label>
              <date-picker
                v-model="formData.end_date"
                type="date"
                :editable="false"
                :clearable="false"
                input-class="form__input"
                value-type="YYYY-MM-DD"
                format="DD MMMM YYYY"
                placeholder="End Date"
                :disabled-date="notBeforeStart"
              ></date-picker>
            </div>
          </div>
          <div class="form-search__row">
            <div class="form-search__double">
              <label class="form__label">Asset Group</label>
              <select class="form__input" v-model="formData.group_id">
                <option value="" v-if="listGroup.length > 1">Show All</option>
                <option value="" v-if="listGroupLoading">Please wait</option>
                <option v-for="row in listGroup" :key="row.id" :value="row.id">
                  {{ row.code_name }}
                </option>
              </select>
            </div>
            <div class="form-search__double">
              <label class="form__label">Ticket Category</label>
              <select class="form__input" v-model="formData.category_id">
                <option value="" v-if="listCategory.length > 1">
                  Show All
                </option>
                <option value="" v-if="listCategoryLoading">Please wait</option>
                <option
                  v-for="row in listCategory"
                  :key="row.id"
                  :value="row.id"
                >
                  {{ row.name }}
                </option>
              </select>
            </div>
          </div>
          <div class="form-search__row">
            <div class="form-search__double">
              <label class="form__label">Status</label>
              <select class="form__input" v-model="formData.status">
                <option value="">Show All</option>
                <option value="0">Open</option>
                <option value="1">In Progress</option>
                <option value="2">Answered</option>
                <option value="3">User Reply</option>
                <option value="4">Closed</option>
              </select>
            </div>
          </div>
          <div class="form-search__action">
            <div class="w-6/12 flex">
              <BaseButton type="submit" buttonClass="secondary"
                >Search</BaseButton
              >
              <BaseButton
                type="button"
                class="mx-2"
                buttonClass="default"
                @click="reset"
                >Reset Search</BaseButton
              >
            </div>
          </div>
        </form>

        <vue-good-table
          :key="tableKey"
          mode="remote"
          theme="polar-bear"
          :rows="tableData.rows"
          :columns="tableData.columns"
          :total-rows="tableData.totalRows"
          :isLoading="tableData.isLoading"
          :sort-options="{
            enabled: false,
          }"
          :pagination-options="{
            enabled: true,
            perPageDropdown: perPageDropdown,
            dropdownAllowAll: false,
            setCurrentPage: tableData.currentPage,
            infoFn: tablePageInfo,
          }"
          styleClass="vgt-table striped bordered"
          @on-page-change="onPageChange"
          @on-per-page-change="onPerPageChange"
        >
          <template slot="table-row" slot-scope="props">
            <span v-if="props.column.field == 'created_by'">
              {{ props.row.created_by.username }}<br />[{{
                props.row.created_by.role.name
              }}]
            </span>
            <span v-else-if="props.column.field == 'status'">
              <span
                :class="parseStatus(props.row.status).color"
                class="flex items-center w-24"
              >
                <font-awesome-icon
                  :icon="['fas', 'circle']"
                  class="mr-1"
                  size="xs"
                />{{ parseStatus(props.row.status).name }}
              </span>
            </span>
            <span v-else-if="props.column.field == 'updated_at'">
              {{ relativeTimeFormat(props.row.updated_at) }}
            </span>
            <span v-else-if="props.column.field == 'uuid'">
              <router-link
                :to="{
                  name: 'SupportTicketView',
                  params: { id: props.row.id },
                }"
                class="
                  w-full
                  button
                  border-gray-600
                  button
                  border border-solid
                  hover:bg-gray-600
                  hover:text-white
                  transition-all
                  duration-200
                  text-center text-gray-600
                "
                ><font-awesome-icon class="mr-1" :icon="['far', 'eye']" />
                View</router-link
              >
            </span>
          </template>
        </vue-good-table>
      </template>
    </CardBase>
  </div>
</template>

<script>
import { showListMixin } from "@/mixins/showListMixin";
import { datePickerMixin } from "@/mixins/datePickerMixin";
import DatePicker from "vue2-datepicker";

export default {
  data() {
    return {
      listGroup: [],
      listGroupLoading: true,
      listCategory: [],
      listCategoryLoading: true,
      listAsset: [],
      tableData: {
        columns: [
          {
            label: "Date",
            field: "created_at",
          },
          {
            label: "Group",
            field: "group.name",
          },
          {
            label: "Category",
            field: "category.name",
          },
          {
            label: "Ticket",
            field: "id",
          },
          {
            label: "User",
            field: "created_by",
          },
          {
            label: "Subject",
            field: "subject",
          },
          {
            label: "Status",
            field: "status",
          },
          {
            label: "Last Updated",
            field: "updated_at",
          },
          {
            label: "",
            field: "uuid",
            thClass: "vgt-table__action",
            tdClass: "vgt-table__action",
          },
        ],
        sort_by: "created_at",
        sort_dir: "desc",
      },
      formData: {
        status: "",
        search: "",
        group_id: "",
        category_id: "",
        start_date: null,
        end_date: null,
      },
    };
  },
  mixins: [showListMixin, datePickerMixin],
  components: {
    DatePicker,
  },
  created() {
    this.getListGroup();
    this.getListCategory();
  },
  methods: {
    getListCategory() {
      this.$http
        .get(process.env.VUE_APP_API_URL + "/ticket-category")
        .then((res) => {
          if (res.status == 200) {
            const response = res.data;

            if (response) {
              this.listCategory = response.data;
            }
          }
        })
        .finally(() => {
          this.listCategoryLoading = false;
        });
    },
    getListGroup() {
      this.listGroupLoading = true;
      this.$http
        .get(process.env.VUE_APP_API_URL + "/group")
        .then((res) => {
          if (res.status == 200) {
            const response = res.data;

            if (response) {
              this.listGroup = response.data;

              if (this.listGroup.length == 1) {
                this.formData.group_id = this.listGroup[0].id;
              }
            }
          }
        })
        .finally(() => {
          this.listGroupLoading = false;
        });
    },
    relativeTimeFormat(date) {
      return this.moment(date).fromNow();
    },
    parseStatus(id) {
      const status = {
        0: {
          name: "Open",
          color: "text-green-500",
        },
        1: {
          name: "In Progress",
          color: "text-blue-600",
        },
        2: {
          name: "Answered",
          color: "text-red-600",
        },
        3: {
          name: "User Reply",
          color: "text-yellow-600",
        },
        4: {
          name: "Closed",
          color: "text-gray-500",
        },
      };

      return status[id];
    },
    dateFormat(date) {
      return this.moment(date).format("DD MMM YYYY - HH:mm");
    },
    reset() {
      this.formData.search = "";
      this.formData.start_date = this.moment(new Date()).format("YYYY-MM-DD");
      this.formData.end_date = this.moment(new Date()).format("YYYY-MM-DD");
      this.formData.status = "";
      this.formData.group_id = "";
      this.formData.category_id = "";
      this.tableData.sort_dir = "desc";
      this.tableData.limit = 10;

      if (this.$route.query.search == undefined) {
        this.tableKey++;
      } else {
        this.tableData.currentPage = 1;
        this.$router.push({ name: "SupportTicketList" }).catch(() => {});
      }
    },
    reloadTable() {
      if (this.formData.search && this.formData.search.length < 3) {
        this.$swal({
          position: "bottom-end",
          showConfirmButton: false,
          toast: true,
          timer: 3000,
          timerProgressBar: true,
          title: "Search query must have at least 3 characters",
          icon: "warning",
        });
      } else {
        this.tableData.currentPage = 1;
        this.$router
          .replace({
            query: {
              search: this.formData.search,
              start_date: this.formData.start_date,
              end_date: this.formData.end_date,
              group_id: this.formData.group_id,
              category_id: this.formData.category_id,
              status: this.formData.status,
              sort_dir: this.tableData.sort_dir,
              tz: new Date().getTime(),
            },
          })
          .catch(() => {});
      }
    },
    loadTableData() {
      this.tableData.isLoading = true;

      const queryParams = this.$route.query;
      let searchParam = "";

      if (queryParams.search != undefined) {
        this.formData = { ...this.formData, ...this.$route.query };
        this.tableData.sort_dir = this.$route.query.sort_dir;
        for (const key in queryParams) {
          if (key != "sort_dir") {
            searchParam += `&${key}=${queryParams[key]}`;
          }
        }
      } else {
        searchParam += `&start_date=${this.formData.start_date}&end_date=${this.formData.end_date}&status=`;
      }

      this.$http
        .get(
          process.env.VUE_APP_API_URL +
            `/ticket?page=${this.tableData.currentPage}&limit=${this.tableData.limit}&sort_by=${this.tableData.sort_by}&sort_dir=${this.tableData.sort_dir}${searchParam}`
        )
        .then((res) => {
          this.tableData.rows = res.data.data;
          this.tableData.totalRows = res.data.total;
        })
        .finally(() => {
          this.tableData.isLoading = false;
        });
    },
    processDeposit(id, status) {
      this.$store.commit("toggleLoading");

      const url =
        process.env.VUE_APP_API_URL + "/deposit/" + id + "/process/" + status;

      this.$http({
        method: "patch",
        url: url,
      })
        .then((res) => {
          if (res.status == 200 || res.status == 201) {
            this.$swal({
              position: "bottom-end",
              showConfirmButton: false,
              toast: true,
              timer: 5000,
              timerProgressBar: true,
              title: res.data.message,
              icon: res.data.icon,
            });
          }
        })
        .catch((error) => {
          if (error.response) {
            this.formError = [];

            if (error.response.status == 409) {
              this.formError[0] = "<pre>" + error.response.data + "</pre>";
            } else {
              for (var err in error.response.data) {
                for (var msg in error.response.data[err]) {
                  this.formError.push(error.response.data[err][msg]);
                }
              }
            }
          }
        })
        .finally(() => {
          this.$store.commit("toggleLoading");

          this.loadTableData();
        });
    },
  },
};
</script>